/**
 * Browser error reporting to BugSink (Sentry protocol).
 *
 * Mirrors the backend posture in `app/core/sentry_scrub.py`, for the same
 * reason: the error tracker has none of the access gating, retention policy or
 * org scoping the audit store has, so a patient name reaching it is a plaintext
 * PHI copy in a system not designed to hold one.
 *
 * This is a DICOM viewer, so two rules are stricter than they would be for an
 * ordinary web app:
 *
 *   - No Session Replay, ever. It records the DOM, and the DOM here is patient
 *     studies and reports. There is no masking configuration that makes
 *     screenshotting a clinical workstation acceptable.
 *   - No tracing. BugSink ingests errors only, so transactions buy nothing and
 *     would ship viewer URLs (query strings included) for healthy sessions.
 *
 * Activation is a single value: SENTRY_DSN, read from `window.RUNTIME_ENV`
 * (injected per deployment by the nginx entrypoint) with a build-time fallback.
 * Empty or unset means no reporting at all.
 */
import * as Sentry from '@sentry/react';

export const REDACTED = '[redacted]';

/**
 * Keys whose value is redacted wherever it appears in an event. Exact match on
 * the normalized name, never substring: "name" is inside `filename`,
 * `funcName` and `displayName`, and redacting those would destroy the stack
 * traces this exists to deliver.
 *
 * Deliberately absent: StudyInstanceUID, patient_id and friends. The audit
 * plane already stores those as `entity_id`, so hiding them here buys nothing
 * and costs the ability to ask which study broke.
 */
const SENSITIVE_KEYS = new Set([
  // Patient PII - kept in step with backend audit_fields._PATIENT_PII
  'patient_name',
  'patient_middle_name',
  'patient_last_name',
  'patientname',
  'email',
  'phone_number',
  'address',
  'policy_number',
  'insurance_provider',
  'cin',
  'spouse_name',
  'emergency_details',
  'birth_date',
  'birthdate',
  'dob',
  // Free-text search: `?search=Benali` is how a surname reaches an error report
  'search',
  'search_term',
  'q',
  'query',
  'query_string',
  'name',
  'first_name',
  'last_name',
  'full_name',
  // Credentials
  'password',
  'token',
  'access_token',
  'refresh_token',
  'authorization',
  'cookie',
  'api_key',
  'secret',
  'session_id',
  // Header spellings. normalizeKey folds "-" to "_", so one entry covers both.
  'x_api_key',
  'x_auth_token',
  'x_csrf_token',
  'csrf_token',
  'proxy_authorization',
  'set_cookie',
  // Whole payloads: a report body is the clinical narrative
  'body',
  'payload',
  'response_content',
  'request_body',
]);

// An event is a bounded tree in practice, but a cyclic or absurdly nested
// object must not turn a crash report into a stack overflow.
const MAX_DEPTH = 12;

const normalizeKey = key =>
  typeof key === 'string'
    ? key.trim().toLowerCase().replace(/^_+/, '').replace(/-/g, '_')
    : null;

const isSensitive = key => {
  const name = normalizeKey(key);
  return name !== null && SENSITIVE_KEYS.has(name);
};

/** Drop the query string from a URL-ish value, keeping the endpoint. */
const stripQuery = value =>
  /^(https?:\/\/|\/)/.test(value) && value.includes('?')
    ? `${value.split('?')[0]}?${REDACTED}`
    : value;

/** Recursively redact sensitive values in an arbitrary event fragment. */
export function scrub(value, depth = 0) {
  if (depth > MAX_DEPTH) {
    return REDACTED;
  }
  if (Array.isArray(value)) {
    return value.map(item => scrub(item, depth + 1));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [
        key,
        isSensitive(key) ? REDACTED : scrub(val, depth + 1),
      ])
    );
  }
  if (typeof value === 'string') {
    return stripQuery(value);
  }
  return value;
}

/**
 * Sentry `beforeSend`. Never throws: a scrubbing failure drops the event rather
 * than shipping it unscrubbed.
 */
export function beforeSend(event) {
  try {
    return scrub(event);
  } catch (e) {
    return null;
  }
}

/** Same treatment for breadcrumbs - fetch/xhr crumbs carry full URLs. */
export function beforeBreadcrumb(breadcrumb) {
  try {
    return scrub(breadcrumb);
  } catch (e) {
    return null;
  }
}

/**
 * Noise that would otherwise evict real bugs from a 10k-event retention window.
 * ResizeObserver fires constantly in a viewport-driven viewer; the rest is
 * browser extensions and user-cancelled requests, none of it our code.
 */
const IGNORE_ERRORS = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  'AbortError',
  'The play() request was interrupted',
  'top.GLOBALS',
];

const DENY_URLS = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
];

/**
 * Start error reporting. No-op without a DSN, which is how every environment
 * that has not opted in stays silent.
 */
export function initSentry({ dsn, environment, release, tenant } = {}) {
  if (!dsn) {
    return false;
  }

  Sentry.init({
    dsn,
    environment: environment || 'unknown',
    release,
    // See the module docstring: BugSink is error-only, and Replay would
    // screenshot patient studies.
    tracesSampleRate: 0,
    sendDefaultPii: false,
    beforeSend,
    beforeBreadcrumb,
    ignoreErrors: IGNORE_ERRORS,
    denyUrls: DENY_URLS,
  });

  // Every tenant reports into the same project, so without this an error list
  // cannot answer "one site or all of them?". CLIENT_NAME is already injected
  // per deployment, so this needs no new chart variable.
  if (tenant) {
    Sentry.setTag('tenant', tenant);
  }

  return true;
}
